import { useMutation } from '@apollo/client';
import { useMelwaysInfo } from '@seek/melways-react';
import { metrics } from '@seek/metrics-js';
import { useTranslations } from '@vocab/react';
import { useEffect } from 'react';

import { browserLogError } from 'src/modules/error-logger/browserLog';
import { updateCTPSubscriptionQL } from 'src/modules/graphql/notifications/preference.graphql';
import { useTypedSelector } from 'src/reducers';
import type {
  UpdateNotificationCtpSubscriptionWithTokenMutation,
  UpdateNotificationCtpSubscriptionWithTokenMutationVariables,
} from 'src/types/graphql';

import Subscription from '../common/subscription';
import PageLoader from '../page-loader';
import { trackSubscribeSuccessPageLoad } from '../tracking/tealium';

import translations from './.vocab';
import resubscribeIcon from './img/resubscribe-icon.png';
import SubscribeError from './subscribe-error';

const Subscribe = ({ token }: { token: string }) => {
  const { language } = useMelwaysInfo();
  const { t } = useTranslations(translations);

  const [subscribeCTPMutation, { error, data }] = useMutation<
    UpdateNotificationCtpSubscriptionWithTokenMutation,
    UpdateNotificationCtpSubscriptionWithTokenMutationVariables
  >(updateCTPSubscriptionQL);

  const country = useTypedSelector(
    (state) => state.page.pageAnalytics.siteCountry,
  );
  const zone = useTypedSelector((store) => store.page.pageAnalytics.zone);
  const locale = `${language}-${country.toUpperCase()}`;
  const isGraphFailure =
    data?.updateNotificationCtpSubscriptionWithToken?.__typename ===
    'UpdateNotificationCtpPreferenceFailure';

  useEffect(() => {
    if (token) {
      subscribeCTPMutation({
        variables: {
          input: {
            token,
            subscribe: true,
            locale,
          },
          locale,
        },
      }).catch((e) => browserLogError(e));
    }
  }, [subscribeCTPMutation, token, language, locale]);

  if (error || isGraphFailure) {
    metrics.count('subscribe-error-page-rendered');
    return <SubscribeError language={language} zone={zone} />;
  }

  if (
    data?.updateNotificationCtpSubscriptionWithToken?.__typename ===
    'UpdateNotificationCtpSubscriptionWithTokenSuccess'
  ) {
    const result = data.updateNotificationCtpSubscriptionWithToken;
    const product = result.product.id;
    const channel = result.subscriptionChannel.id;
    trackSubscribeSuccessPageLoad(product, channel);
    metrics.count('subscribe-page-rendered');

    return (
      <Subscription
        title={t('subscribe-subscription-title')}
        cardTitle={t('subscribe-subscription-card-title')}
        body={t('subscribe-subscription-body')}
        linkMessage={t('subscribe-subscription-link-message')}
        icon={resubscribeIcon}
      />
    );
  }

  return <PageLoader />;
};

export default Subscribe;
